import { Form, Formik } from "formik";
import React, { FC, ReactElement, useEffect, useState } from "react";
import { DATE_FILTER_ELEMENT_SYSNAME, REPORTS_FILTER_DATE_TIME_CURRENT_WEEK, REPORTS_FILTER_DATE_TIME_DATE_RANGE, REPORTS_FILTER_DATE_TIME_LAST_FOUR_WEEKS, REPORTS_FILTER_DATE_TIME_LAST_THREE_DAYS, REPORTS_FILTER_DATE_TIME_LAST_TWO_WEEKS, REPORTS_FILTER_DATE_TIME_LAST_WEEK, REPORTS_FILTER_DATE_TIME_SPECIFIC_DATE, REPORTS_FILTER_DATE_TIME_YESTERDAY, STATIC_DATE_ELEMENT } from "../../../constants/element-type";
import { HOURS_WORKED, REPORT_STILL_ON_SITE } from "../../../constants/menu-types";
import { TranslationKeys } from "../../../contexts/TranslationKeys";
import { useLocale } from "../../../contexts/localeContext";
import { useFilter } from "../../../contexts/table/FilterContext";
import { useTable } from "../../../contexts/table/TableContext";
import { Filter } from "../../../models/Filter";
import WizardSideBarStep from "../../../models/WizardSideBarStep";
import Wizard from "../../wizard/wizard";
import { Category } from "./category-step";
import DynamicDateWizard from "./dynamic-date-wizard";
import StaticDateWizard from "./static-date-wizard";
import { FilterModel } from "../FilterModel";

interface DateFilterWizardProps {
    defaultContent: ReactElement[];
    defaultSteps: WizardSideBarStep[];
    onReset: (event: any) => void;
    onClose: () => void;
    filterModalTrigger: string;
}

const DateFilterWizard: FC<DateFilterWizardProps> = ({ onClose, defaultContent, defaultSteps, onReset, filterModalTrigger }) => {
    const { translate } = useLocale();
    const [step, setStep] = useState<number>(1);
    const [category, setCategory] = useState<any>("");
    const [categoryName, setCategoryName] = useState<any>(translate.byKey(TranslationKeys.pending));
    const filterContext = useFilter();
    const tableContext = useTable();
    const [currentItem, setCurrentItem] = useState<any>(
        {
            typeElementSysname: '',
            categoryElementSysname: '',
            startDate: undefined,
            endDate: undefined,
        }
    );
    const steps: WizardSideBarStep[] = [
        ...defaultSteps,
        {
            title: translate.byKey(TranslationKeys.filterCategory),
            description: categoryName,
            substeps: []
        }
    ];
    const [categoryFilter, setCategoryFilter] = useState<FilterModel | undefined>(undefined);

    useEffect(() => {
        let date: string[];
        let startDate: Date | undefined = undefined;
        let endDate: Date | undefined = undefined;

        if (filterContext.filters != null) {
            filterContext.filters.map((filters) => {
                if (filters.key === "dateOnly" && filters.selected.length > 0 && (filters.selected[0].elementSysname === REPORTS_FILTER_DATE_TIME_DATE_RANGE || filters.selected[0].elementSysname === REPORTS_FILTER_DATE_TIME_SPECIFIC_DATE)) {
                    date = filters.selected.length > 0 ? filters.selected[0].value.split(';') : [];
                    startDate = date.length > 0 ? new Date(date[0].trim()) : undefined;
                    endDate = date.length > 1 ? new Date(date[1].trim()) : undefined;
                }
            })
        }

        if (startDate || endDate) {
            const categoryElementSysname = (startDate && endDate) ? REPORTS_FILTER_DATE_TIME_DATE_RANGE : REPORTS_FILTER_DATE_TIME_SPECIFIC_DATE
            const categoryName = (startDate && endDate) ? translate.byKey(TranslationKeys.dateRange) : translate.byKey(TranslationKeys.specificDate);
            const initialValues = {
                typeElementSysname: DATE_FILTER_ELEMENT_SYSNAME,
                categoryElementSysname: categoryElementSysname,
                startDate: startDate,
                endDate: endDate
            };
            setCurrentItem(initialValues);
            setCategory(categoryElementSysname);
            setCategoryName(categoryName);
        }

        if (tableContext.menuId === REPORT_STILL_ON_SITE) {
            setCategoryFilter({
                elementSysname: [REPORTS_FILTER_DATE_TIME_SPECIFIC_DATE],
                include: true
            })
        } else if (tableContext.menuId === HOURS_WORKED) {
            const dateElements: string[] = [
                REPORTS_FILTER_DATE_TIME_CURRENT_WEEK,
                REPORTS_FILTER_DATE_TIME_LAST_TWO_WEEKS,
                REPORTS_FILTER_DATE_TIME_LAST_THREE_DAYS,
                REPORTS_FILTER_DATE_TIME_LAST_FOUR_WEEKS,
                REPORTS_FILTER_DATE_TIME_LAST_WEEK,
                REPORTS_FILTER_DATE_TIME_YESTERDAY
            ];

            setCategoryFilter({
                elementSysname: dateElements,
                include: true
            })
        }
    }, [])

    const handleSubmit = (values: any) => {
        const columnId: any = tableContext.tableDescriptor.find(column => column.accessor === 'dateOnly')?.id;

        if (values.categoryElementSysname === REPORTS_FILTER_DATE_TIME_SPECIFIC_DATE) {
            const startDate: Date = values.startDate;

            saveWithDates(startDate, columnId, undefined);
        }
        else if (values.categoryElementSysname === REPORTS_FILTER_DATE_TIME_DATE_RANGE) {
            const startDate: Date = values.startDate;
            const endDate: Date = values.endDate;

            saveWithDates(startDate, columnId, endDate);
        }
        else {
            filterContext.updateFilters(columnId, "0", "0", undefined, values.categoryElementSysname);
        }

        onClose();
    }

    const saveWithDates = (startDate: Date, columnId: any, endDate?: Date) => {
        const stringStartDate: string = `${startDate.getFullYear().toString()}-${(startDate.getMonth() + 1).toString().padStart(2, '0')}-${startDate.getDate().toString().padStart(2, '0')}`;
        let elementSysname: string = REPORTS_FILTER_DATE_TIME_SPECIFIC_DATE;

        const alreadyAppliedDateFilter: Filter | undefined = filterContext.filters.find(filter => filter.key === "dateOnly")
        let alreadyAppliedDate: string = "";
        let toBeAppliedDate = stringStartDate;

        if (alreadyAppliedDateFilter) {
            alreadyAppliedDate = alreadyAppliedDateFilter.selected.length > 0 ? alreadyAppliedDateFilter.selected[0].value : "";
        }

        if (stringStartDate && endDate) {
            const stringEndDate: string = `${endDate.getFullYear().toString()}-${(endDate.getMonth() + 1).toString().padStart(2, '0')}-${endDate.getDate().toString().padStart(2, '0')}`;
            toBeAppliedDate = `${stringStartDate};${stringEndDate}`;
            elementSysname = REPORTS_FILTER_DATE_TIME_DATE_RANGE;
        }

        //Date filter are special on transaction they should not remove them, so if it's a same date don't update the filters.
        //Need to look at it in the future but for now it's safe since transaction is the only report that uses this filter-modal
        if (alreadyAppliedDate !== toBeAppliedDate) {
            filterContext.updateFilters(columnId, toBeAppliedDate, toBeAppliedDate, undefined, elementSysname);
        }
    }

    const onNext = (event: any) => {
        setCategory(event.elementSysname);
        setCategoryName(event.name);
    }

    const onBack = (event: any) => {
        // If the modal was trigger by clicking filter chip block going back to the filter type step
        if (filterModalTrigger && step === 1) {
            return;
        }
        onReset(event)
        setStep(step - 1);
    }

    const content: ReactElement[] = [
        ...defaultContent,
        <Category
            key={"wizard-2"}
            onBack={onBack}
            onNext={onNext}
            categoryFilter={categoryFilter} 
            filterModalTrigger={filterModalTrigger}/>
    ];

    const onLocalReset = () => {
        setCategory("");
        setCategoryName(translate.byKey(TranslationKeys.pending));
    }

    const getWizard = () => {
        if (category === REPORTS_FILTER_DATE_TIME_SPECIFIC_DATE || category === REPORTS_FILTER_DATE_TIME_DATE_RANGE) {
            return <DynamicDateWizard
                onClose={onClose}
                defaultContent={content}
                defaultSteps={steps}
                selectedCategory={categoryName} 
                onBack={onLocalReset}/>
        }
        else if (STATIC_DATE_ELEMENT.includes(category)) {
            return <StaticDateWizard
                defaultContent={content}
                defaultSteps={steps}
                onBack={onLocalReset}
                onClose={onClose}
                selectedCategory={categoryName} />
        }

        return <Wizard name={translate.byKey(TranslationKeys.dataFilters)}
            step={1}
            onClose={onClose}
            sidebarSteps={steps}>
            {content}
        </Wizard>
    }

    return <Formik
        enableReinitialize={true}
        initialValues={currentItem}
        onSubmit={handleSubmit}>
        <Form>
            {getWizard()}
        </Form>
    </Formik>
}

export default DateFilterWizard;